import useDebounce from 'core/utils/hooks/custom-hooks';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import type { Dayjs } from 'dayjs';
import { useFiltersStore } from '@features/regional-overview/components/filters/filters.store';
import { RegionalOverviewFilterType } from '@features/regional-overview/regional-overview.models';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect } from 'react';
import { useDigitalTrapsStore } from '../../digital-traps.zustand';
import type { FilterContext } from '../../domain';

const EVENT_PREFIX = 'Digital Traps - ';

interface DigitalTrapsFiltersProps {
  context: FilterContext;
}

export const useDigitalTrapsFilters = ({ context }: DigitalTrapsFiltersProps) => {
  const setSelectedDate = useDigitalTrapsStore(state => state.setSelectedDate);

  const setValue = useFiltersStore(state => state.setValue);
  const applyValues = useFiltersStore(state => state.applyValues);
  const textSearchValue = useFiltersStore(state => state.values[RegionalOverviewFilterType.TEXT_SEARCH]);

  const segmentTracking = useSegmentTracking();
  const debouncedSearch = useDebounce(textSearchValue, 500);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(RegionalOverviewFilterType.TEXT_SEARCH, e.target.value);
    },
    [setValue]
  );

  const handleDateChange = useCallback(
    (date: Dayjs) => {
      setSelectedDate(date);
      segmentTracking.track(`${EVENT_PREFIX}Pest Pressure Analysis - ${context} date picker`, {
        selectedDate: date?.format('YYYY-MM-DD')
      });
    },
    [context, segmentTracking, setSelectedDate]
  );

  useEffect(() => {
    applyValues();
    if (typeof debouncedSearch === 'string' && debouncedSearch.trim().length > 0) {
      segmentTracking.track(`${EVENT_PREFIX}Pest Pressure Analysis - ${context} search`, {
        searchQuery: debouncedSearch
      });
    }
  }, [applyValues, context, debouncedSearch, segmentTracking]);

  return {
    handleSearchChange,
    handleDateChange
  };
};
